// src/v2/playbooks/PlaybookRunPage.jsx
//
// G-34-S2: Playbook runner — roadmap view for a single run.
//
// Route: /playbooks/runs/:runId
//
// Sections:
//   1. Breadcrumb back link
//   2. Hero card: icon, title, target label, phase N of M, owner, progress bar + phase dots, launched-from provenance
//   3. "Next human step" callout (active runs only, when a current check exists)
//   4. Phase accordion with check rows
//   5. Cancel button (editor/admin, active runs only)

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { T } from "../../app/tokens.js";
import {
  getPlaybookRun,
  advancePlaybookCheck,
  cancelPlaybookRun,
} from "../../data/playbooksClient.js";
import { showToast } from "../../components/toastService.jsx";
import { log } from "../../utils/logger.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ICON_MAP = {
  "closure":       "⇄",
  "relocation":    "⇄",
  "office closure":"⇄",
  "renewal":       "↻",
  "lease renewal": "↻",
  "consolidation": "⤢",
  "market consolidation": "⤢",
  "opening":       "＋",
  "new office":    "＋",
};

function runIcon(name) {
  const n = (name || "").toLowerCase();
  for (const [key, icon] of Object.entries(ICON_MAP)) {
    if (n.includes(key)) return icon;
  }
  return "▶";
}

function fmtDate(d) {
  if (!d) return null;
  const raw = d instanceof Date ? d.toISOString() : String(d);
  const iso  = raw.slice(0, 10);
  const dt   = new Date(iso + "T12:00:00Z");
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// ─── StatusPill ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const map = {
    active:      { bg: T.blueSoft,  color: T.blue,          label: "Active"      },
    in_progress: { bg: T.blueSoft,  color: T.blue,          label: "In progress" },
    completed:   { bg: T.greenSoft, color: T.green,         label: "Completed"   },
    cancelled:   { bg: T.lightGray, color: T.mid,           label: "Cancelled"   },
  };
  const s = map[status] ?? map.active;
  return (
    <span style={{
      fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase",
      letterSpacing: "0.04em", padding: "2px 9px", borderRadius: 6,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── CheckStatusIcon ──────────────────────────────────────────────────────────

// isActive: true when this check is the active (pending/in_progress) check
// in the current phase — shows a filled blue dot instead of an empty ring.
function CheckStatusIcon({ status, isActive }) {
  if (status === "done") {
    return (
      <div style={{
        width: 20, height: 20, borderRadius: "50%",
        background: T.green, color: T.white,
        display: "grid", placeItems: "center",
        flexShrink: 0,
      }}>
        <Check size={11} strokeWidth={3} />
      </div>
    );
  }
  if (isActive || status === "in_progress") {
    return (
      <div style={{
        width: 20, height: 20, borderRadius: "50%",
        background: T.blue, color: T.white,
        display: "grid", placeItems: "center",
        flexShrink: 0,
        fontSize: T.fsXs,
      }}>
        ●
      </div>
    );
  }
  return (
    <div style={{
      width: 20, height: 20, borderRadius: "50%",
      border: `2px solid ${T.border}`,
      flexShrink: 0,
    }} />
  );
}

// ─── Phase card (accordion) ───────────────────────────────────────────────────
//
// Current phase is expanded by default; done/todo phases start collapsed.
// Clicking the header toggles the check list.

function PhaseCard({ phase, isReadOnly, onAdvance, advancingCheckId }) {
  const phaseStatus = phase.status; // "done" | "current" | "todo"

  // Default: expand the current phase, collapse done/todo.
  const [expanded, setExpanded] = useState(phaseStatus === "current");

  const statusChip = {
    done:    { bg: T.greenSoft, color: T.green, label: "Done"        },
    current: { bg: T.blueSoft,  color: T.blue,  label: "In progress" },
    todo:    { bg: T.lightGray, color: T.light, label: "To do"       },
  }[phaseStatus] ?? { bg: T.lightGray, color: T.light, label: "To do" };

  return (
    <div
      data-testid={`phase-card-${phase.id}`}
      style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 13, marginBottom: 12, overflow: "hidden",
      }}
    >
      {/* Phase header — clickable to toggle */}
      <button
        type="button"
        data-testid={`phase-header-${phase.id}`}
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 9,
          padding: "14px 16px",
          background: "transparent", border: "none", cursor: "pointer",
          fontWeight: T.fwBold, fontSize: T.fsBase5, textAlign: "left",
        }}
      >
        {/* Chevron */}
        <span style={{
          display: "inline-block",
          transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.15s ease",
          fontSize: T.fsXs, color: T.mid, flexShrink: 0,
        }}>
          ▶
        </span>

        <span style={{ flex: 1, minWidth: 0 }}>{phase.name}</span>

        <span style={{
          fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase",
          letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 6,
          background: statusChip.bg, color: statusChip.color, flexShrink: 0,
        }}>
          {statusChip.label}
        </span>
      </button>

      {/* Check rows — collapsed when not expanded */}
      {expanded && (
      <div style={{ padding: "0 16px 12px" }}>
      {(phase.checks || []).map((check, idx) => {
        // A check is "actionable" when its phase is current and it's pending/in_progress.
        const isActionable = phaseStatus === "current" &&
          (check.status === "pending" || check.status === "in_progress");
        const isDone = check.status === "done";
        const isAdvancing = advancingCheckId === check.id;

        return (
          <div
            key={check.id}
            data-testid={`check-row-${check.id}`}
            style={{
              display: "flex", alignItems: "center", gap: 11,
              padding: "9px 0",
              borderTop: idx === 0 ? "none" : `1px solid ${T.lightGray}`,
              opacity: isDone ? 0.65 : 1,
            }}
          >
            <CheckStatusIcon status={check.status} isActive={isActionable} />

            {/* Check label (API field: label, not name) */}
            <span style={{
              fontWeight: isActionable ? T.fwBold : T.fwSemibold,
              fontSize: T.fsSm5, flex: 1, minWidth: 0,
              color: isDone ? T.mid : T.dark,
            }}>
              {check.label}
            </span>

            {/* → in Radar badge */}
            {check.emitsRadar && (
              <span style={{
                fontSize: T.fsXxs5, fontWeight: T.fwBold,
                color: T.blue, background: T.blueSoft,
                borderRadius: 5, padding: "2px 7px",
                flexShrink: 0,
              }}>
                → in Radar
              </span>
            )}

            {/* Owner chip (API field: ownerName, not owner) */}
            {check.ownerName && (
              <span style={{ fontSize: T.fsXs, color: T.light, flexShrink: 0 }}>
                {check.ownerName}
              </span>
            )}

            {/* Advance button — only for actionable check, editor/admin, active run */}
            {isActionable && !isReadOnly && (
              <button
                type="button"
                data-testid={`advance-check-${check.id}`}
                onClick={() => onAdvance(check.id)}
                disabled={isAdvancing}
                style={{
                  width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                  background: isAdvancing ? T.border : T.blue,
                  border: "none", cursor: isAdvancing ? "not-allowed" : "pointer",
                  display: "grid", placeItems: "center",
                  color: T.white,
                }}
                title="Mark complete"
              >
                {isAdvancing
                  ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
                  : <Check size={12} strokeWidth={3} />
                }
              </button>
            )}
          </div>
        );
      })}
      </div>
      )}
    </div>
  );
}

// ─── CancelConfirm ────────────────────────────────────────────────────────────

function CancelConfirm({ onConfirm, onDismiss, cancelling }) {
  return (
    <div
      data-testid="cancel-confirm"
      style={{
        position: "fixed", inset: 0,
        background: T.modalOverlay,
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100,
      }}
    >
      <div style={{
        background: T.white, borderRadius: 14, padding: "24px 28px",
        boxShadow: T.shadowModal, width: 360,
      }}>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
          Cancel this run?
        </div>
        <div style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 20, lineHeight: 1.5 }}>
          The run will be marked as cancelled and kept as a read-only record.
          This cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onDismiss}
            style={{
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: 8, padding: "8px 16px",
              fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.mid,
              cursor: "pointer",
            }}
          >
            Keep open
          </button>
          <button
            type="button"
            data-testid="cancel-confirm-btn"
            onClick={onConfirm}
            disabled={cancelling}
            style={{
              background: T.red, border: "none", borderRadius: 8, padding: "8px 16px",
              fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.white,
              cursor: cancelling ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {cancelling && <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />}
            Cancel run
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PlaybookRunPage ──────────────────────────────────────────────────────────

export function PlaybookRunPage({ currentRole }) {
  const { runId }  = useParams();
  const navigate   = useNavigate();
  const canEdit    = currentRole === "editor" || currentRole === "admin";

  const [run,            setRun]            = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [advancingId,    setAdvancingId]    = useState(null);
  const [cancelOpen,     setCancelOpen]     = useState(false);
  const [cancelling,     setCancelling]     = useState(false);

  const fetchRun = useCallback(() => {
    if (!runId) return;
    setLoading(true);
    getPlaybookRun(runId)
      .then((r) => { setRun(r); setLoading(false); })
      .catch((err) => {
        log.error("PlaybookRunPage: failed to load run", err);
        setError("Could not load this run. It may have been removed.");
        setLoading(false);
      });
  }, [runId]);

  useEffect(() => { fetchRun(); }, [fetchRun]);

  async function handleAdvance(checkId) {
    if (!run || advancingId) return;
    setAdvancingId(checkId);
    try {
      await advancePlaybookCheck(run.id, checkId, true);
      showToast("Check completed.", { type: "success" });
      fetchRun();
    } catch (err) {
      log.error("PlaybookRunPage: advance check failed", err);
      showToast("Failed to advance check. Please try again.", { type: "error" });
    } finally {
      setAdvancingId(null);
    }
  }

  async function handleCancel() {
    if (!run || cancelling) return;
    setCancelling(true);
    try {
      await cancelPlaybookRun(run.id);
      showToast("Run cancelled.", { type: "success" });
      fetchRun();
      setCancelOpen(false);
    } catch (err) {
      log.error("PlaybookRunPage: cancel failed", err);
      showToast("Failed to cancel run. Please try again.", { type: "error" });
    } finally {
      setCancelling(false);
    }
  }

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        maxWidth: 860, margin: "0 auto", padding: "40px 24px",
        display: "flex", alignItems: "center", gap: 10, color: T.mid,
      }}>
        <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: T.fsSm5 }}>Loading run…</span>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ fontSize: T.fsSm5, color: T.red }}>{error ?? "Run not found."}</div>
        <button
          type="button"
          onClick={() => navigate("/playbooks/runs")}
          style={{
            marginTop: 12, background: "transparent", border: "none",
            color: T.blue, cursor: "pointer", fontSize: T.fsSm5,
            fontWeight: T.fwBold, padding: 0,
          }}
        >
          ← Back to all runs
        </button>
      </div>
    );
  }

  const isActive   = run.status === "active" || run.status === "in_progress";
  const isReadOnly = !isActive || !canEdit;
  const phases     = run.phases ?? [];
  const icon       = runIcon(run.name);

  // Find next check: first pending/in_progress check in the current phase.
  // API check statuses are: 'pending' | 'in_progress' | 'done' (no 'current').
  let nextCheck = null;
  for (const phase of phases) {
    if (phase.status !== "current") continue;
    const cur = (phase.checks ?? []).find(
      (c) => c.status === "pending" || c.status === "in_progress"
    );
    if (cur) { nextCheck = cur; break; }
  }

  // Progress
  const phaseCount   = run.phaseCount  ?? phases.length ?? 1;
  const phasesDone   = run.phasesDone  ?? phases.filter((p) => p.status === "done").length ?? 0;
  const progressPct  = phaseCount > 0 ? Math.round((phasesDone / phaseCount) * 100) : 0;

  return (
    <div
      data-testid="playbook-run-page"
      style={{ maxWidth: 860, margin: "0 auto", padding: "20px 24px 70px" }}
    >
      {/* ── Breadcrumb ──────────────────────────────────────────────── */}
      <div style={{ fontSize: T.fsXs5, color: T.light, fontWeight: T.fwBold, marginBottom: 6 }}>
        <button
          type="button"
          data-testid="back-to-runs"
          onClick={() => navigate("/playbooks/runs")}
          style={{
            background: "transparent", border: "none", padding: 0,
            color: T.mid, cursor: "pointer", fontSize: T.fsXs5, fontWeight: T.fwBold,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <ArrowLeft size={12} /> Playbooks › Runs
        </button>
      </div>

      {/* ── Hero card ───────────────────────────────────────────────── */}
      <div
        data-testid="run-hero-card"
        style={{
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: "17px 19px",
          marginBottom: 14, boxShadow: T.shadowCardXs,
        }}
      >
        {/* Top row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: T.blueSoft, color: T.blue,
            display: "grid", placeItems: "center",
            fontSize: T.fsLg5, flexShrink: 0,
          }}>
            {icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: T.fsLg7, fontWeight: T.fwBold, margin: 0, lineHeight: 1.2 }}>
              {run.name}
            </h1>
            {run.sourceLabel && (
              <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
                {run.sourceLabel}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", fontSize: T.fsXs5, color: T.mid, flexShrink: 0 }}>
            <strong style={{ color: T.dark, fontSize: T.fsSm5 }}>
              Phase {Math.min(phasesDone + 1, phaseCount)} of {phaseCount}
            </strong>
            {run.owner && <div style={{ marginTop: 2 }}>owner {run.owner}</div>}
            <div style={{ marginTop: 4 }}>
              <StatusPill status={run.status} />
            </div>
          </div>
        </div>

        {/* Launched-from provenance */}
        {run.launchedFrom && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            fontSize: T.fsXs5, color: T.mid,
            background: T.lightGray, border: `1px solid ${T.border}`,
            borderRadius: 8, padding: "5px 10px", marginTop: 12,
          }}>
            <span style={{
              fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase",
              background: T.blueSoft, color: T.blue,
              borderRadius: 5, padding: "2px 6px",
            }}>
              {run.launchedFromType ?? "Manual"}
            </span>
            {run.sourceType === "decision" && run.sourceId ? (
              <a
                data-testid="provenance-decision-link"
                href={`/radar/decisions?highlight=${encodeURIComponent(run.sourceId)}`}
                style={{ color: T.mid, textDecoration: "underline" }}
              >
                {run.sourceLabel}
              </a>
            ) : run.sourceType === "signal" && run.sourceId ? (
              <a
                data-testid="provenance-radar-link"
                href={`/infocus-radar?highlight=${encodeURIComponent(run.sourceId)}`}
                style={{ color: T.mid, textDecoration: "underline" }}
              >
                {run.launchedFrom}
              </a>
            ) : (
              run.launchedFrom
            )}
          </div>
        )}

        {run.sourceType === "decision" && run.context && (
          <div
            data-testid="decision-context-card"
            style={{
              marginTop: 12, padding: "10px 12px", borderRadius: 9,
              background: T.blueSoft, border: `1px solid ${T.border}`,
              color: T.dark, fontSize: T.fsSm,
            }}
          >
            <strong>Decision handoff · {run.templateVariant ?? "FULL"}</strong>
            <div style={{ marginTop: 4 }}>
              {run.context.decision?.text ?? run.context.decisionText ?? run.sourceLabel}
            </div>
            {(run.context.subject?.locationAlias || run.context.subject?.locationId || run.locationId) && (
              <div style={{ marginTop: 3, color: T.mid }}>
                Subject office: {run.context.subject?.locationAlias ?? run.context.subject?.locationId ?? run.locationId}
              </div>
            )}
            {run.context.target?.date && (
              <div style={{ marginTop: 3, color: T.mid }}>
                Target: {fmtDate(run.context.target.date)}
              </div>
            )}
          </div>
        )}

        {/* Progress bar */}
        <div style={{
          height: 7, borderRadius: 4,
          background: T.lightGray, marginTop: 12, position: "relative",
        }}>
          <div style={{
            height: "100%", borderRadius: 4,
            background: run.status === "completed" ? T.green : T.blue,
            width: `${run.status === "completed" ? 100 : progressPct}%`,
            transition: "width .3s ease",
          }} />
        </div>

        {/* Phase dots */}
        <div style={{ display: "flex", gap: 5, marginTop: 9 }}>
          {Array.from({ length: phaseCount }).map((_, i) => {
            const done = i < phasesDone;
            const cur  = i === phasesDone && isActive;
            return (
              <div
                key={i}
                style={{
                  flex: 1, height: 6, borderRadius: 3,
                  background: done
                    ? T.green
                    : cur
                    ? T.blue
                    : T.border,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* ── Next human step callout ─────────────────────────────────── */}
      {isActive && nextCheck && (
        <div
          data-testid="next-step-callout"
          style={{
            display: "flex", alignItems: "center", gap: 11,
            background: T.blueSoft, border: `1px solid ${T.blueSoftBorder}`,
            borderRadius: 12, padding: "12px 15px", marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: "0.05em",
              textTransform: "uppercase", color: T.blue, marginBottom: 1,
            }}>
              Next human step
            </div>
            <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase5, marginTop: 1 }}>
              {nextCheck.label}
            </div>
            {nextCheck.subLabel && (
              <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 1 }}>
                {nextCheck.subLabel}
              </div>
            )}
          </div>
          {nextCheck.emitsRadar && (
            <Link
              data-testid="live-in-radar-badge"
              to={
                nextCheck.radarActionId
                  ? `/infocus-radar?highlight=${encodeURIComponent(nextCheck.radarActionId)}`
                  : "/infocus-radar"
              }
              style={{
                fontSize: T.fsXxs5, fontWeight: T.fwBold,
                color: T.blue, background: T.white,
                borderRadius: 6, padding: "4px 9px", flexShrink: 0,
                textDecoration: "none",
              }}
            >
              Live in Radar →
            </Link>
          )}
        </div>
      )}

      {/* ── Phase accordion ─────────────────────────────────────────── */}
      {phases.map((phase) => (
        <PhaseCard
          key={phase.id}
          phase={phase}
          isReadOnly={isReadOnly}
          onAdvance={handleAdvance}
          advancingCheckId={advancingId}
        />
      ))}

      {/* ── Cancel button ────────────────────────────────────────────── */}
      {isActive && canEdit && (
        <div style={{ marginTop: 8, textAlign: "right" }}>
          <button
            type="button"
            data-testid="cancel-run-btn"
            onClick={() => setCancelOpen(true)}
            style={{
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: 8, padding: "7px 14px",
              fontSize: T.fsSm5, fontWeight: T.fwBold,
              color: T.mid, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <X size={13} /> Cancel run
          </button>
        </div>
      )}

      {/* ── Cancel confirm dialog ──────────────────────────────────── */}
      {cancelOpen && (
        <CancelConfirm
          onConfirm={handleCancel}
          onDismiss={() => setCancelOpen(false)}
          cancelling={cancelling}
        />
      )}
    </div>
  );
}
